import { useEffect, useState } from "react";
import { Lifebuoy, PaperPlaneTilt, Eye, PhoneOutgoing, SunHorizon, Phone, Copy, CaretDown, CaretUp, Compass } from "@phosphor-icons/react";
import { toast } from "sonner";
import client from "../../lib/api";

const fmt = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "—");
const Tile = ({ testid, children }) => <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid={testid}>{children}</div>;
const RunBtn = ({ onClick, disabled, testid, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} className="text-[11px] text-[#FF7038] hover:text-[#FF5A1F] disabled:opacity-50 inline-flex items-center gap-1 whitespace-nowrap" data-testid={testid}>{children}</button>
);

// Per-trial row: who, when, and a 5-line save-the-sale call script you can copy.
function TrialRow({ u }) {
  const [open, setOpen] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(u.script.map((l, i) => `${i + 1}. ${l}`).join("\n")); toast.success("Script copied"); } catch { toast.error("Couldn't copy"); }
  };
  const tried = Object.values(u.usage || {}).filter(Boolean).length;
  return (
    <div className="py-1.5 border-b border-white/[0.04] last:border-0" data-testid={`trial-row-${u.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-neutral-300 truncate">{u.company || u.name || u.email} <span className="text-neutral-600">· {u.plan || "plan"} · {tried}/6 tried{u.alerted ? " · texted" : ""}</span></span>
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-neutral-500">{fmt(u.trial_ends_at)}</span>
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-[#FF7038] hover:text-[#FF5A1F] inline-flex items-center gap-0.5" data-testid={`trial-script-toggle-${u.id}`}>
            <Phone size={12} /> Script {open ? <CaretUp size={10} /> : <CaretDown size={10} />}
          </button>
        </span>
      </div>
      {open && (
        <div className="mt-2 rounded-lg bg-black/40 p-3" data-testid={`trial-script-${u.id}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">Save-the-sale call · {u.phone || "no phone on file"}</span>
            <button type="button" onClick={copy} className="text-[11px] text-neutral-400 hover:text-white inline-flex items-center gap-1" data-testid={`trial-script-copy-${u.id}`}><Copy size={11} /> Copy</button>
          </div>
          <ol className="space-y-1.5 text-[12px] leading-snug text-neutral-200">
            {u.script.map((line, i) => <li key={i} className="flex gap-2"><span className="text-[#FF5A1F] font-semibold shrink-0">{i + 1}.</span><span>{line}</span></li>)}
          </ol>
        </div>
      )}
    </div>
  );
}

// Four follow-up safety nets: card-page drop-off texts, trial-ending heads-up + call script, day-3 check-in, day-one funnel email.
export default function FollowUpCard() {
  const [drop, setDrop] = useState(null);
  const [trial, setTrial] = useState(null);
  const [dayOne, setDayOne] = useState(null);
  const [day3, setDay3] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => {
    client.get("/admin/alerts/dropoff/stats?days=7").then((r) => setDrop(r.data)).catch(() => {});
    client.get("/admin/alerts/trial-heads-up/preview").then((r) => setTrial(r.data)).catch(() => {});
    client.get("/admin/alerts/day-one/preview").then((r) => setDayOne(r.data)).catch(() => {});
    client.get("/admin/alerts/day3/preview").then((r) => setDay3(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { const { data } = await fn(); toast.success(typeof msg === "function" ? msg(data) : msg); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong"); }
    finally { setBusy(""); }
  };
  return (
    <div className="card-surface p-5" data-testid="followup-card">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-neutral-500 mb-3"><Lifebuoy size={13} /> Save-the-sale follow-ups</div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Tile testid="followup-dropoff">
          <div className="flex items-center justify-between gap-2">
            <div className="text-white font-semibold text-sm inline-flex items-center gap-1.5"><PhoneOutgoing size={15} className="text-[#FF5A1F]" /> Card-page drop-off text</div>
            <RunBtn onClick={() => run("drop", () => client.post("/admin/alerts/dropoff/run-now"), (d) => `Checked ${d.checked} · texted ${d.sms} · emailed ${d.email}`)} disabled={!!busy} testid="followup-dropoff-run">Run now</RunBtn>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">Reached Stripe, no card 30 min later → one text (email if no phone) with a one-tap link back to the card page. Every 15 min, once per person.</p>
          {drop && (
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              {[["Nudged 7d", drop.nudged], ["Came back", drop.recovered], ["Waiting", drop.pending]].map(([k, v]) => (
                <div key={k} className="rounded-lg bg-black/40 p-2"><div className="text-[10px] uppercase tracking-widest text-neutral-500">{k}</div><div className="text-lg font-semibold text-white" data-testid={`dropoff-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div></div>
              ))}
            </div>
          )}
        </Tile>
        <Tile testid="followup-trial">
          <div className="flex items-center justify-between gap-2">
            <div className="text-white font-semibold text-sm inline-flex items-center gap-1.5"><SunHorizon size={15} className="text-[#FFD24C]" /> Trial ending heads-up + call script</div>
            <RunBtn onClick={() => run("trial", () => client.post("/admin/alerts/trial-heads-up/run-now"), (d) => `Sent ${d.sent} heads-up text${d.sent === 1 ? "" : "s"}`)} disabled={!!busy} testid="followup-trial-run">Run now</RunBtn>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">The day before a trial's first charge you get the carrier's name, phone and plan. Tap <b className="text-neutral-400">Script</b> for a 5-line call built from what they've actually tried.</p>
          <div className="mt-3 text-xs" data-testid="followup-trial-list">
            {!trial ? null : trial.upcoming.length === 0
              ? <div className="text-neutral-500">No trials ending in the next 3 days.</div>
              : trial.upcoming.slice(0, 6).map((u) => <TrialRow key={u.id} u={u} />)}
          </div>
        </Tile>
        <Tile testid="followup-day3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-white font-semibold text-sm inline-flex items-center gap-1.5"><Compass size={15} className="text-[#38bdf8]" /> Day-3 check-in email</div>
            <RunBtn onClick={() => run("day3", () => client.post("/admin/alerts/day3/run-now"), (d) => `Checked ${d.checked} · emailed ${d.sent}`)} disabled={!!busy} testid="followup-day3-run">Run now</RunBtn>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">72 h after the card goes on file: one email naming the one feature they haven't tried (loads → drivers → payroll → IFTA → invoices → vault) with a 2-minute walkthrough. Once per carrier.</p>
          {day3 && (
            <>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                {[["Due now", day3.due.length], ["Coming up", day3.upcoming], ["Sent 30d", day3.sent_30d]].map(([k, v]) => (
                  <div key={k} className="rounded-lg bg-black/40 p-2"><div className="text-[10px] uppercase tracking-widest text-neutral-500">{k}</div><div className="text-lg font-semibold text-white" data-testid={`day3-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</div></div>
                ))}
              </div>
              {day3.due.length > 0 && (
                <div className="mt-2 text-xs" data-testid="day3-due-list">
                  {day3.due.slice(0, 4).map((u) => (
                    <div key={u.id} className="flex items-center justify-between gap-2 py-1 border-b border-white/[0.04] last:border-0">
                      <span className="text-neutral-300 truncate">{u.company || u.email}</span>
                      <span className="text-neutral-500 whitespace-nowrap">→ {u.feature || "all tried"}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </Tile>
        <Tile testid="followup-dayone">
          <div className="flex items-center justify-between gap-2">
            <div className="text-white font-semibold text-sm inline-flex items-center gap-1.5"><Eye size={15} className="text-[#00D4AA]" /> Day-one /start funnel email</div>
            <RunBtn onClick={() => run("day1", () => client.post("/admin/alerts/day-one/send-now"), (d) => d.sent ? `Emailed to ${d.to}` : `Not sent: ${d.reason}`)} disabled={!!busy || !dayOne?.first_at} testid="followup-dayone-send"><PaperPlaneTilt size={12} /> Email now</RunBtn>
          </div>
          <p className="text-[11px] text-neutral-500 mt-1">Fires once, 24 h after the first ad click lands on /start: click → stayed → form → submitted → card page → card, with the biggest leak.</p>
          <div className="mt-3 text-xs" data-testid="followup-dayone-status">
            {!dayOne ? null : !dayOne.first_at
              ? <div className="text-neutral-500">Waiting for the first /start click.</div>
              : <div className="text-neutral-300">{dayOne.text}</div>}
          </div>
        </Tile>
      </div>
    </div>
  );
}
